import React, { useState, useEffect } from 'react';
import { X, Plus, Pencil, Trash2 } from 'lucide-react';

const FLAG_LABELS = {
  nut: { icon: '🥜', label: 'Nut' },
  gluten: { icon: '🌾', label: 'Gluten' },
  dairy: { icon: '🥛', label: 'Dairy' },
  egg: { icon: '🥚', label: 'Egg' },
  shellfish: { icon: '🦐', label: 'Shellfish' },
  garlic: { icon: '🧄', label: 'Garlic' },
  tomato: { icon: '🍅', label: 'Tomato' },
  vegan: { icon: '🌱', label: 'Vegan' },
  vegetarian: { icon: '🥕', label: 'Vegetarian' },
  // (29 Oct 2026, decision 156) Diets.
  dairy_free: { icon: '🥛', label: 'Dairy-free' },
  gluten_free: { icon: '🌾', label: 'Gluten-free' },
  pescatarian: { icon: '🐟', label: 'Pescatarian' },
  keto: { icon: '🥑', label: 'Keto' },
  other: { icon: '📝', label: 'Other' },
};
// (29 Oct 2026, decision 156) Three categories, in this order: Allergies, Diets, Aversions.
// The category is chosen first; each offers only its own items. Allergy and Aversion keep
// every item that isn't a diet; Diet offers Dairy-free, Gluten-free, Vegetarian, Vegan,
// Pescatarian, Keto and Other. Kept the same in dietaryFlagsRoutes.js (the server checks it).
const DIETARY_CATEGORIES = [
  { value: 'allergy', label: 'Allergy', pill: 'bg-rose-100 text-rose-800' },
  { value: 'diet', label: 'Diet', pill: 'bg-sky-100 text-sky-800' },
  { value: 'aversion', label: 'Aversion', pill: 'bg-green-100 text-green-800' },
];
const NON_DIET_ITEMS = ['nut', 'gluten', 'dairy', 'egg', 'shellfish', 'garlic', 'tomato', 'other'];
const ITEMS_BY_CATEGORY = {
  allergy: NON_DIET_ITEMS,
  diet: ['dairy_free', 'gluten_free', 'vegetarian', 'vegan', 'pescatarian', 'keto', 'other'],
  aversion: NON_DIET_ITEMS,
};
const categoryOf = (value) => DIETARY_CATEGORIES.find((c) => c.value === value) || DIETARY_CATEGORIES[2];
// The categories an existing entry can be moved between (an Other entry: all three).
const categoriesFor = (flagType) => DIETARY_CATEGORIES.filter((c) => ITEMS_BY_CATEGORY[c.value].includes(flagType));


// (8 Sep 2026) GA-proxy dietary-flags dialog — Pass 3 of the proxy-entry
// design agreed 8 Sep. Deliberately IMMEDIATE-commit per action (add,
// edit, remove each fire straight away), unlike the member's own
// Settings > Profile version (DietaryFlagsSection in SettingsModal.jsx),
// which batches everything behind a Save Changes button. That batching
// exists for consistency with the rest of the Profile tab's save
// lifecycle; this is a standalone admin dialog with no such lifecycle, so
// immediate-commit — matching MembersTab.jsx's own add/remove pattern —
// is the simpler, more consistent fit here.
//
// Only ever opened for a status='active' member — MembersTab's "Manage"
// button is only shown for active members, since proxy-entry requires a
// users.id to attach a flag to (truly-pending, never-signed-in members
// are out of scope for now, per the design decision agreed 8 Sep).
const ManageMemberDietaryFlags = ({ calendarId, member, apiBaseUrl, authToken, onStatusMessage, onClose }) => {
  const [flags, setFlags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newFlagType, setNewFlagType] = useState('');
  const [newSeverity, setNewSeverity] = useState('');
  const [newOtherText, setNewOtherText] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [editingFlagId, setEditingFlagId] = useState(null);
  const [savingFlagId, setSavingFlagId] = useState(null);
  const [removingFlagId, setRemovingFlagId] = useState(null);
  const [confirmingRemoveId, setConfirmingRemoveId] = useState(null);

  const baseUrl = `${apiBaseUrl}/api/calendars/${calendarId}/members/${member.id}/dietary-flags`;

  const refetch = () => {
    setIsLoading(true);
    fetch(baseUrl, { headers: { 'Authorization': `Bearer ${authToken}` } })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setFlags(data))
      .catch(() => setFlags([]))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member.id]);

  const usedTypes = flags.map((f) => f.flag_type);
  // (29 Oct 2026, decision 156) The items of the chosen category, less those already recorded.
  const availableTypes = (ITEMS_BY_CATEGORY[newSeverity] || []).filter((t) => t === 'other' || !usedTypes.includes(t));

  const handleAdd = async () => {
    if (!newSeverity || !newFlagType) {
      onStatusMessage(!newSeverity ? 'Please choose Allergy, Diet or Aversion first' : 'Please choose an item', 'error');
      return;
    }
    if (newFlagType === 'other' && !newOtherText.trim()) {
      onStatusMessage('Please describe the "Other" entry', 'error');
      return;
    }
    setIsAdding(true);
    try {
      const res = await fetch(baseUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          flag_type: newFlagType,
          severity: newSeverity,
          other_text: newFlagType === 'other' ? newOtherText.trim() : undefined,
        })
      });
      const data = await res.json();
      if (res.ok) {
        onStatusMessage('Flag added', 'success');
        setNewFlagType('');
        setNewSeverity('');
        setNewOtherText('');
        refetch();
      } else {
        onStatusMessage(data.error || 'Failed to add flag', 'error');
      }
    } catch (err) {
      onStatusMessage('Failed to add flag', 'error');
    } finally {
      setIsAdding(false);
    }
  };

  const handleChangeSeverity = async (flag, severity) => {
    setSavingFlagId(flag.id);
    try {
      const res = await fetch(`${baseUrl}/${flag.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          severity,
          other_text: flag.flag_type === 'other' ? flag.other_text : undefined,
        })
      });
      const data = await res.json();
      if (res.ok) {
        onStatusMessage('Flag updated', 'success');
        refetch();
      } else {
        onStatusMessage(data.error || 'Failed to update flag', 'error');
      }
    } catch (err) {
      onStatusMessage('Failed to update flag', 'error');
    } finally {
      setSavingFlagId(null);
      setEditingFlagId(null);
    }
  };

  // (8 Sep 2026) Replaced window.confirm — it's un-stylable and looks
  // jarring next to the app's own confirmation modals (e.g. "Delete
  // Calendar?" / "Delete Event?" in DashboardStep.jsx). Same two-step
  // pattern: the trash button sets confirmingRemoveId, and this function
  // — called only from the styled confirm modal below — does the actual
  // removal.
  const handleRemoveConfirmed = async (flagId) => {
    setConfirmingRemoveId(null);
    setRemovingFlagId(flagId);
    try {
      const res = await fetch(`${baseUrl}/${flagId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const data = await res.json();
      if (res.ok) {
        onStatusMessage('Flag removed', 'success');
        refetch();
      } else {
        onStatusMessage(data.error || 'Failed to remove flag', 'error');
      }
    } catch (err) {
      onStatusMessage('Failed to remove flag', 'error');
    } finally {
      setRemovingFlagId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-bold text-gray-900">Allergies, diets &amp; aversions</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4">{member.email}</p>

        {isLoading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : (
          <>
            {flags.length === 0 ? (
              <p className="text-sm text-gray-400 italic mb-3">None recorded yet.</p>
            ) : (
              <ul className="space-y-2 mb-4">
                {flags.map((f) => (
                  <li key={f.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                    <span className="text-sm text-gray-800 flex items-center gap-2 flex-wrap">
                      <span>{FLAG_LABELS[f.flag_type]?.icon || '📝'}</span>
                      {f.flag_type === 'other' ? f.other_text : FLAG_LABELS[f.flag_type]?.label}
                      {editingFlagId === f.id ? (
                        <select
                          autoFocus
                          value={f.severity}
                          onChange={(e) => handleChangeSeverity(f, e.target.value)}
                          onBlur={() => setEditingFlagId(null)}
                          disabled={savingFlagId === f.id}
                          className="text-xs border border-gray-300 rounded-full px-2 py-0.5"
                        >
                          {categoriesFor(f.flag_type).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      ) : (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${categoryOf(f.severity).pill}`}>
                          {categoryOf(f.severity).label}
                        </span>
                      )}
                      {/* (23 Oct 2026, 8.44, decision 125) Whom the member marked it for,
                          when not both of a couple (set in their own Settings). */}
                      {f.applies_to === 'primary' && (
                        <span className="text-xs text-gray-500">Member only</span>
                      )}
                      {f.applies_to === 'secondary' && (
                        <span className="text-xs text-gray-500">Secondary contact only</span>
                      )}
                      {/* (8 Sep 2026) Provenance — shown here too so the GA
                          can see whether a flag was self-reported or
                          entered by (possibly a different) GA earlier. */}
                      {f.enteredByName && (
                        <span className="text-xs italic text-gray-400">Entered by {f.enteredByName}</span>
                      )}
                    </span>
                    <span className="flex items-center gap-2 ml-2">
                      {/* (29 Oct 2026, decision 156) Only where the entry could go in another category. */}
                      {categoriesFor(f.flag_type).length > 1 && (
                        <button
                          onClick={() => setEditingFlagId(f.id)}
                          className="text-gray-400 hover:text-gray-600 transition"
                          title="Change category"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => setConfirmingRemoveId(f.id)}
                        disabled={removingFlagId === f.id}
                        className="text-red-500 hover:text-red-700 transition disabled:opacity-50"
                        title="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {/* (29 Oct 2026, decision 156) Captioned "Category" and "Item" (Neil). The category first, then the item from its list. */}
            {(
              <div className="flex flex-col gap-2 border border-gray-200 rounded-lg p-3">
                <div className="flex gap-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-gray-600">Category</span>
                    <select
                      value={newSeverity}
                      onChange={(e) => {
                        const sev = e.target.value;
                        setNewSeverity(sev);
                        if (newFlagType && !(ITEMS_BY_CATEGORY[sev] || []).includes(newFlagType)) setNewFlagType('');
                      }}
                      className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                    >
                      <option value="">Please select…</option>
                      {DIETARY_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </label>
                  <label className="flex-1 min-w-0 flex flex-col gap-1">
                    <span className="text-xs font-medium text-gray-600">Item</span>
                    <select
                      value={newFlagType}
                      onChange={(e) => setNewFlagType(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                    >
                      <option value="">{newSeverity ? 'Please select…' : 'Choose the category first'}</option>
                      {availableTypes.map((t) => (
                        <option key={t} value={t}>{FLAG_LABELS[t].icon} {FLAG_LABELS[t].label}</option>
                      ))}
                    </select>
                  </label>
                </div>
                {newFlagType === 'other' && (
                  <input
                    type="text"
                    value={newOtherText}
                    onChange={(e) => setNewOtherText(e.target.value)}
                    placeholder="Describe (e.g. sesame)"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                )}
                {/* (29 Oct 2026) Not disabled for a missing choice (decision 40): pressing it says what is missing. */}
                <button
                  onClick={handleAdd}
                  disabled={isAdding}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4" />
                  {isAdding ? 'Adding…' : 'Add'}
                </button>
              </div>
            )}
          </>
        )}

        <button
          onClick={onClose}
          className="w-full mt-4 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Close
        </button>
      </div>

      {confirmingRemoveId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Remove this flag?</h3>
            <p className="text-gray-600 mb-4">This action cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmingRemoveId(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRemoveConfirmed(confirmingRemoveId)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageMemberDietaryFlags;
